"use client";

import { type FocusEvent, type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "@local/utils/src/cn";
import { useOutsideClickCapture } from "@local/utils/src/useOutsideClickCapture";
import Image from "next/image";
import { getCdnUrl } from "@local/utils/src/getCdnUrl";
import { getLanguageForMarket, localizeMarketPath } from "@local/i18n/src/languages";
import { getT } from "@local/i18n/src/t";
import { chrome } from "@local/i18n/src/catalogs/chrome";
import type { NavigationMainItem } from "../types";
import type { Route } from "next";
import { Link } from "@local/ui/src/components/ui/Link";
import { AnchorNavLink } from "./AnchorNavLink";
import { Modal } from "@local/ui/src/components/ui/Modal";
import dynamic from "next/dynamic";

// Dynamically import static page components for modal display
const AboutUs = dynamic(() => import("../../StaticPages/AboutUs").then(mod => mod.AboutUs), {
  ssr: false
});
const VeteransCharities = dynamic(() => import("../../StaticPages/VeteransCharities").then(mod => mod.VeteransCharities), {
  ssr: false
});
const BaerskinStories = dynamic(() => import("../../StaticPages/BaerskinStories").then(mod => mod.BaerskinStories), {
  ssr: false
});
const PrivacyPolicy = dynamic(() => import("../../StaticPages/PrivacyPolicy").then(mod => mod.PrivacyPolicy), {
  ssr: false
});
const TermsConditions = dynamic(() => import("../../StaticPages/TermsConditions").then(mod => mod.TermsConditions), {
  ssr: false
});
const ShippingReturnsRefunds = dynamic(() => import("../../StaticPages/ShippingReturnsRefunds").then(mod => mod.ShippingReturnsRefunds), {
  ssr: false
});
const SecurePayments = dynamic(() => import("../../StaticPages/SecurePayments").then(mod => mod.SecurePayments), {
  ssr: false
});
const Accessibility = dynamic(() => import("../../StaticPages/Accessibility").then(mod => mod.Accessibility), {
  ssr: false
});
interface MenuDesktopItemProps {
  item: NavigationMainItem;
  id?: string;
  market: string;
  /**
   * Whether this item's mega-menu panel is open. Owned by the parent
   * (HeaderMenuDesktop) so only one panel can be open at a time. Applies to the
   * mega-menu branch only; the plain-link and modal branches ignore it.
   */
  open?: boolean;
  /** Request this item become the single open item (hover / Enter / Space). */
  onOpen?: () => void;
  /** Request this item close (mouseleave / blur / Escape / outside-click / navigation). */
  onClose?: () => void;
}
export const MenuDesktopItem = ({
  item,
  id,
  market,
  open = false,
  onOpen,
  onClose
}: MenuDesktopItemProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const t = getT(getLanguageForMarket(market), {
    chrome
  });
  const liRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const reactId = useId();
  const panelId = `mega-menu-panel-${reactId}`;
  const close = useCallback(() => onClose?.(), [onClose]);
  useOutsideClickCapture(liRef, close);

  // Escape dismisses the mega-menu regardless of where focus is. A document-level
  // listener (vs. an element-scoped onKeyDown) is required so ESC also closes a
  // panel opened purely by hover, where focus never enters the <li> (WCAG 1.4.13).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      onClose?.();
      // Only pull focus back to the trigger if focus was inside the menu
      // (keyboard-opened path); for a hover-opened panel leave focus untouched.
      if (liRef.current?.contains(document.activeElement)) {
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const handleTriggerKeyDown = useCallback((e_0: KeyboardEvent<HTMLButtonElement>) => {
    if (e_0.key === "Enter" || e_0.key === " ") {
      e_0.preventDefault();
      onOpen?.();
    }
  }, [onOpen]);
  const handleMouseLeave = useCallback(() => {
    if (liRef.current?.contains(document.activeElement)) return;
    onClose?.();
  }, [onClose]);
  const handleBlur = useCallback((e_1: FocusEvent<HTMLLIElement>) => {
    if (!liRef.current) return;
    if (!liRef.current.contains(e_1.relatedTarget as Node | null)) {
      onClose?.();
    }
  }, [onClose]);

  /**
   * Determines if a URL should open in a modal instead of navigating
   */
  const shouldOpenModal = (url?: string) => {
    if (!url) return false;
    return url.includes("about-us") || url.includes("our-story") || url.includes("privacy-policy") || url.includes("terms-and-conditions") || url.includes("shipping-returns-and-refunds") || url.includes("secure-payments") || url.includes("veterans-charities") || url.includes("baerskin-stories") || url.includes("accessibility");
  };

  /**
   * Gets the appropriate modal content based on URL
   */
  const getModalContent = (url_0?: string) => {
    if (!url_0) return null;
    switch (true) {
      case url_0.includes("about-us") || url_0.includes("our-story"):
        return <AboutUs market={market} />;
      case url_0.includes("privacy-policy"):
        return <PrivacyPolicy market={market} />;
      case url_0.includes("terms-and-conditions"):
        return <TermsConditions market={market} />;
      case url_0.includes("shipping-returns-and-refunds"):
        return <ShippingReturnsRefunds market={market} />;
      case url_0.includes("secure-payments"):
        return <SecurePayments market={market} />;
      case url_0.includes("veterans-charities"):
        return <VeteransCharities market={market} />;
      case url_0.includes("baerskin-stories"):
        return <BaerskinStories market={market} />;
      case url_0.includes("accessibility"):
        return <Accessibility market={market} />;
      default:
        return null;
    }
  };
  const modalContent = getModalContent(item.url);
  if (!item.subitems || item.subitems.length === 0) {
    // If this link should open a modal
    if (shouldOpenModal(item.url)) {
      return <li className="relative h-full list-none">
          <button onClick={() => setModalOpen(true)} className="flex h-full min-h-[56px] items-center" type="button">
            {item.name}
          </button>
          <Modal open={modalOpen} toggle={setModalOpen} contentClassName="max-w-4xl">
            {modalContent}
          </Modal>
        </li>;
    }

    // Regular link behavior
    return <li className="relative h-full list-none">
        {item.anchor ? <AnchorNavLink anchor={item.anchor} className="flex h-full min-h-[56px] items-center">
            {item.name}
          </AnchorNavLink> : <Link href={((item.url && localizeMarketPath(item.url, market)) ?? "#") as Route} className="flex h-full min-h-[56px] items-center" prefetch={false}>
            {item.name}
          </Link>}
      </li>;
  }
  if (!item.url && (!item.subitems || item.subitems.length === 0)) {
    return null;
  }
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the <li> wraps the trigger and disclosed panel; mouse/focus listeners coordinate menu disclosure (mouseleave/blur close when focus is outside; Escape closes via a document-level listener above)
    <li ref={liRef} className="h-full list-none" onMouseLeave={handleMouseLeave} onBlur={handleBlur}>
      <div className="relative h-full">
        <button ref={triggerRef} id={id} type="button" aria-haspopup="true" aria-expanded={open} aria-controls={panelId} onMouseEnter={onOpen} onKeyDown={handleTriggerKeyDown} className={cn("flex h-full min-h-[56px] items-center",
        // Hover "safe triangles": while this trigger is hovered, two invisible
        // wedges extend the hover zone outward so the pointer can travel from
        // the narrow trigger down into the full-width panel without the <li>'s
        // mouseleave firing. Each wedge's vertical edge is anchored to the
        // button edge and it only protrudes sideways at the BOTTOM (a point at
        // the trigger row, widening downward), so it never covers the adjacent
        // nav item's trigger, which is what previously blocked switching menus.
        "hover:before:z-nav hover:before:absolute hover:before:top-0 hover:before:-left-12 hover:before:h-full hover:before:w-12 hover:before:content-[''] hover:before:[clip-path:polygon(100%_0,100%_100%,0_100%)]", "hover:after:z-nav hover:after:absolute hover:after:top-0 hover:after:-right-12 hover:after:h-full hover:after:w-12 hover:after:content-[''] hover:after:[clip-path:polygon(0_0,0_100%,100%_100%)]")}>
          {item.name}
        </button>
      </div>

      <div id={panelId} aria-label={t("chrome.navigationOf", {
        name: item.name
      })} hidden={!open} className="z-dropdown absolute inset-x-0 top-full text-sm text-gray-500">
        {/* Presentational element used to render the bottom shadow, if we put the shadow on the actual panel it pokes out the top, so we use this shorter element to hide the top of the shadow */}
        <div className="absolute inset-0 top-1/2 bg-white shadow-xl" aria-hidden="true" />

        <div className="absolute bottom-0 z-50 h-10 w-full bg-gradient-to-b from-transparent to-white" />

        <div className="relative max-h-[600px] overflow-auto bg-white pb-10">
          <div className="mx-auto max-w-7xl px-4">
            {item.url && <div className="pt-6">
                <Link href={localizeMarketPath(item.url, market) as Route} prefetch={false} onClick={close} className="inline-flex items-center gap-2 text-base font-bold tracking-wider text-gray-900 uppercase underline-offset-4 hover:underline focus-visible:underline">
                  {t("chrome.seeAllProductsIn", {
                  name: item.name
                })}
                  <span aria-hidden="true">→</span>
                </Link>
              </div>}
            <div className="grid grid-cols-1 items-start gap-x-4 gap-y-1 py-4">
              <div className="grid gap-x-8 gap-y-1 md:grid-cols-2 lg:grid-cols-4">
                {item.subitems?.map((subitem, i) => <div key={`item-${i}`}>
                    {subitem.url ? <Link className="font-semibold tracking-wider uppercase" onClick={close} href={localizeMarketPath(subitem.url, market) as Route} prefetch={false}>
                        {subitem.name}
                      </Link> : <span className="font-semibold tracking-wider uppercase">
                        {subitem.name}
                      </span>}
                    {subitem.image && subitem.url && <Link href={localizeMarketPath(subitem.url, market) as Route} prefetch={false} onClick={close} className="my-2 block overflow-hidden" style={{
                    aspectRatio: "2 / 1"
                  }}>
                        <Image alt={subitem.name} src={getCdnUrl({
                      src: subitem.image?.src || "",
                      width: subitem.image?.width || 400
                    })} width={subitem.image?.width} height={subitem.image?.height} className="hover:opacity-75" />
                      </Link>}
                    <ul aria-label={t("chrome.navigationOf", {
                    name: subitem.name
                  })}>
                      {subitem.subitems?.map((subsubitem, j) => subsubitem.url ? <li key={`item-${i}-subitem-${j}`} className="flex">
                            <Link className="py-1 font-medium" href={localizeMarketPath(subsubitem.url, market) as Route} prefetch={false} onClick={close}>
                              {subsubitem.name}
                            </Link>
                          </li> : null)}
                    </ul>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};